// ViewResults.jsx
import React, { useState, useEffect } from "react";
import { db } from "../../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "./AddResult.css";

export default function ViewResults() {
    const [department, setDepartment] = useState("");
    const [program, setProgram] = useState("");
    const [session, setSession] = useState("");
    const [semester, setSemester] = useState("");
    const [rollNo, setRollNo] = useState("");
    const [semesters, setSemesters] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Fixed fields (order enforced)
    const fixedStart = ["Roll #", "Name"];
    const fixedEnd = ["Total", "%age", "SGPA", "CGPA", "Remarks"];

    // Fetch available semesters dynamically
    useEffect(() => {
        const fetchSemesters = async () => {
            if (department && program && session) {
                try {
                    const sessionRef = collection(db, "results", department, program, session);
                    const semSnapshot = await getDocs(sessionRef);
                    const semList = semSnapshot.docs.map((doc) => doc.id);
                    setSemesters(semList);
                } catch (error) {
                    console.error("Error fetching semesters:", error);
                }
            }
        };
        fetchSemesters();
    }, [department, program, session]);

    const fetchResults = async () => {
        if (!department || !program || !session || !semester || !rollNo) {
            setMessage({ type: "error", text: "Please select all filters and enter Roll #" });
            return;
        }

        setLoading(true);
        setMessage("");
        setResults([]);

        try {
            const colRef = collection(db, "results", department, program, session, semester);
            const q = query(colRef, where("Roll #", "==", rollNo));
            const snapshot = await getDocs(q);

            const data = snapshot.docs.map((doc) => doc.data());
            setResults(data);

            if (data.length === 0) {
                setMessage({ type: "error", text: "No results found for this Roll # in selected filters." });
            }
        } catch (error) {
            console.error("Error fetching results:", error);
            setMessage({ type: "error", text: "Failed to fetch results." });
        }

        setLoading(false);
    };

    // Helper: dynamically build column order
    const getOrderedFields = (row) => {
        if (!row) return [];

        const allKeys = Object.keys(row);

        // Subjects = all keys that are not in fixedStart or fixedEnd or any variation of Sr. No.
        const subjectKeys = allKeys.filter((key) => {
            const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, ""); // normalize
            return (
                !fixedStart.includes(key) &&
                !fixedEnd.includes(key) &&
                !lowerKey.startsWith("srno") &&
                !lowerKey.startsWith("sr")
            );
        });

        // Sort subjects alphabetically
        subjectKeys.sort((a, b) => a.localeCompare(b));

        // Final ordered fields
        return [...fixedStart, ...subjectKeys, ...fixedEnd].filter((f) => allKeys.includes(f));
    };

    return (
        <div className="container">
            <h2 className="title">Search Student Result</h2>

            {/* Filters */}
            <div className="filters">
                <select className="select" value={department} onChange={(e) => setDepartment(e.target.value)}>
                    <option value="">Select Department</option>
                    <option value="CS">Computer Science</option>
                </select>

                <select className="select" value={program} onChange={(e) => setProgram(e.target.value)}>
                    <option value="">Select Program</option>
                    <option value="BCS">BSC</option>
                    <option value="BSE">BSE</option>
                </select>

                <select className="select" value={session} onChange={(e) => setSession(e.target.value)}>
                    <option value="">Select Session</option>
                    <option value="Spring 25">Spring 25</option>
                    <option value="Fall 25">Fall 25</option>
                </select>

                <select className="select" value={semester} onChange={(e) => setSemester(e.target.value)}>
                    <option value="">Select Semester</option>
                    {[1,2,3,4,5,6,7,8].map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>

                <input
                    className="select"
                    type="text"
                    placeholder="Enter Roll No"
                    value={rollNo}
                    onChange={(e) => setRollNo(e.target.value)}
                />
            </div>

            {/* Fetch Button */}
            <button className="uploadBtn" onClick={fetchResults} disabled={loading}>
                {loading ? "Searching..." : "Search Result"}
            </button>

            {/* Message */}
            {message && (
                <div className={`message ${message.type === "error" ? "error" : "success"}`}>
                    {message.text}
                </div>
            )}

            {/* Table */}
            <div className="tableWrapper">
                {results.length > 0 && (
                    <table className="dataTable">
                        <thead>
                        <tr>
                            {getOrderedFields(results[0]).map((field, idx) => (
                                <th key={idx}>{field}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {results.map((row, idx) => (
                            <tr key={idx}>
                                {getOrderedFields(row).map((field, i) => (
                                    <td key={i}>{row[field]}</td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
